import type { ImgHTMLAttributes } from "react";

/**
 * Shape returned by vite-imagetools when imported with `?...&as=picture`.
 * Sources are keyed by mime subtype (avif, webp, jpeg, png). Each value is a
 * srcset string like "url1 640w, url2 1024w, url3 1600w".
 */
export type PictureSource = {
  sources: Record<string, string>;
  img: { src: string; w: number; h: number };
};

type Props = Omit<ImgHTMLAttributes<HTMLImageElement>, "src" | "srcSet"> & {
  image: PictureSource;
  /** Sizes attribute for the responsive `<img>`. Default: `100vw`. */
  sizes?: string;
  /** Wrapper class applied to the `<picture>` element. */
  pictureClassName?: string;
  /** React 19 name; forwarded as lowercase HTML attribute for React 18 compat. */
  fetchPriority?: "high" | "low" | "auto";
};

// Preferred format order — browsers pick the first one they can decode.
const ORDER = ["avif", "webp", "jpeg", "png"] as const;

export function Picture({
  image,
  alt,
  sizes = "100vw",
  pictureClassName,
  loading = "lazy",
  decoding = "async",
  className,
  fetchPriority,
  ...rest
}: Props) {
  const { sources, img } = image;
  const keys = ORDER.filter((k) => sources[k]);
  // React 18 warns on the camelCase `fetchPriority` prop; forward the
  // lowercase HTML attribute instead so the browser hint still applies.
  const extraAttrs = fetchPriority ? { fetchpriority: fetchPriority } : {};
  return (
    <picture className={pictureClassName}>
      {keys.slice(0, -1).map((k) => (
        <source key={k} type={`image/${k}`} srcSet={sources[k]} sizes={sizes} />
      ))}
      <img
        {...rest}
        {...extraAttrs}
        src={img.src}
        srcSet={sources[keys[keys.length - 1] ?? "jpeg"]}
        sizes={sizes}
        width={img.w}
        height={img.h}
        alt={alt ?? ""}
        loading={loading}
        decoding={decoding}
        className={className}
      />
    </picture>
  );
}

export default Picture;